import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, Check, SlidersHorizontal, X } from 'lucide-react';

import type {
  DailyPulseAnswerValue,
  DailyPulseQuestion,
} from 'services/api';

import './DailyPulse.css';

export type DailyPulseConfirmationKind = 'skip' | 'close' | 'change-date';

export interface DailyPulseJourneySegment {
  key: string;
  status: 'handled' | 'skipped' | 'current' | 'pending';
}

export interface DailyPulseFormProps {
  questions: DailyPulseQuestion[];
  answers: Record<string, DailyPulseAnswerValue>;
  note: string;
  targetDate: string;
  today: string;
  initialOutcome?: 'saved' | 'skipped' | null;
  journeySegments?: DailyPulseJourneySegment[];
  progressLabel?: string;
  saving?: boolean;
  saved?: boolean;
  dirty?: boolean;
  error?: string;
  confirmation?: DailyPulseConfirmationKind | null;
  noteOnly?: boolean;
  onAnswer: (key: string, value: DailyPulseAnswerValue | undefined) => void;
  onNoteChange: (note: string) => void;
  onSave: () => void;
  onSkip: () => void;
  onClose: () => void;
  onCustomize: () => void;
  onFillAnother?: () => void;
  onCancelConfirmation: () => void;
  onConfirmDiscard: () => void;
}

const parseDateOnly = (value: string) => {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day);
};

const formatDate = (value: string) => new Intl.DateTimeFormat('en-US', {
  weekday: 'long',
  month: 'long',
  day: 'numeric',
}).format(parseDateOnly(value));

const answeredCount = (answers: Record<string, DailyPulseAnswerValue>) => Object.keys(answers).length;

const metricError = (question: DailyPulseQuestion, value: DailyPulseAnswerValue | undefined) => {
  if (value === undefined || question.response_type === 'rating' || question.response_type === 'boolean') return '';
  if (question.response_type === 'time') {
    return typeof value === 'string' && /^([01]\d|2[0-3]):[0-5]\d$/.test(value) ? '' : 'Enter a valid time.';
  }
  if (typeof value !== 'number' || !Number.isFinite(value)) return question.response_type === 'duration'
    ? 'Enter whole hours and minutes (0–59).'
    : 'Enter a valid number.';
  if (question.response_type === 'duration' && !Number.isInteger(value)) return 'Enter a whole number of minutes.';
  const unit = question.response_type === 'duration' ? ' total minutes' : question.unit ? ` ${question.unit}` : '';
  if (question.min_value != null && value < question.min_value) return `Enter ${question.min_value}${unit} or more.`;
  if (question.max_value != null && value > question.max_value) return `Enter ${question.max_value}${unit} or less.`;
  const step = question.step ?? 1;
  const steps = (value - (question.min_value ?? 0)) / step;
  if (Math.abs(steps - Math.round(steps)) > 0.000001) return `Use increments of ${step}.`;
  return '';
};

const durationParts = (value: DailyPulseAnswerValue | undefined) => {
  if (typeof value === 'number') return [String(Math.floor(value / 60)), String(value % 60)];
  return typeof value === 'string' ? value.split(':') : ['', ''];
};

const DurationControl = ({ question, value, onChange }: {
  question: DailyPulseQuestion;
  value: DailyPulseAnswerValue | undefined;
  onChange: (value: DailyPulseAnswerValue | undefined) => void;
}) => {
  const [parts, setParts] = useState(() => durationParts(value));
  const lastValue = useRef(value);
  useEffect(() => {
    if (value !== lastValue.current) setParts(durationParts(value));
    lastValue.current = value;
  }, [value]);
  const error = metricError(question, value);
  const hintId = `daily-pulse-metric-${question.key}`;
  return (
    <div className="daily-pulse-metric">
      <div className="daily-pulse-metric__field">
        {['hours', 'minutes'].map((unit, index) => (
          <label className="daily-pulse-duration__part" key={unit}>
            <input
              type="text"
              inputMode="numeric"
              aria-label={`${question.prompt} ${unit}`}
              aria-describedby={error ? hintId : undefined}
              aria-invalid={Boolean(error)}
              placeholder="—"
              value={parts[index] ?? ''}
              onChange={(event) => {
                const updated = [...parts];
                updated[index] = event.target.value;
                setParts(updated);
                const empty = updated.every((part) => !part);
                const valid = updated.every((part) => /^\d*$/.test(part)) && Number(updated[1]) < 60;
                const next = empty ? undefined : valid ? Number(updated[0]) * 60 + Number(updated[1]) : updated.join(':');
                lastValue.current = next;
                onChange(next);
              }}
            />
            <span>{unit}</span>
          </label>
        ))}
      </div>
      {error ? <span id={hintId} className="daily-pulse-metric__error">{error}</span> : null}
    </div>
  );
};

const MetricControl = ({ question, value, onChange }: {
  question: DailyPulseQuestion;
  value: DailyPulseAnswerValue | undefined;
  onChange: (value: DailyPulseAnswerValue | undefined) => void;
}) => {
  const [raw, setRaw] = useState(value === undefined ? '' : String(value));
  const lastValue = useRef(value);
  useEffect(() => {
    if (value !== lastValue.current) setRaw(value === undefined ? '' : String(value));
    lastValue.current = value;
  }, [value]);
  const isTime = question.response_type === 'time';
  const unit = question.unit;
  const error = metricError(question, value);
  const hintId = `daily-pulse-metric-${question.key}`;
  return (
    <div className="daily-pulse-metric">
      <div className="daily-pulse-metric__field">
        <input
          type={isTime ? 'time' : 'text'}
          inputMode={isTime ? undefined : 'decimal'}
          aria-label={question.prompt}
          aria-describedby={error || isTime ? hintId : undefined}
          aria-invalid={Boolean(error)}
          value={raw}
          placeholder={isTime ? undefined : 'Not answered'}
          onChange={(event) => {
            const text = event.target.value;
            setRaw(text);
            // Keep incomplete numeric drafts visible; never coerce an empty field to zero.
            const next = text === '' ? undefined : !isTime && /^-?(?:\d+\.?\d*|\.\d+)$/.test(text)
              && Number.isFinite(Number(text)) ? Number(text) : text;
            lastValue.current = next;
            onChange(next);
          }}
        />
        {unit ? <span>{unit}</span> : null}
      </div>
      {error || isTime ? (
        <span id={hintId} className={error ? 'daily-pulse-metric__error' : 'daily-pulse__scale-hint'}>
          {error || 'Your local time'}
        </span>
      ) : null}
    </div>
  );
};

const DailyPulseConfirmation = ({
  kind,
  answers,
  note,
  targetDate,
  today,
  onCancel,
  onConfirm,
}: {
  kind: DailyPulseConfirmationKind;
  answers: Record<string, DailyPulseAnswerValue>;
  note: string;
  targetDate: string;
  today: string;
  onCancel: () => void;
  onConfirm: () => void;
}) => {
  const keepEditingRef = useRef<HTMLButtonElement>(null);
  const count = answeredCount(answers);
  const dateLabel = formatDate(targetDate);

  useEffect(() => {
    keepEditingRef.current?.focus();
  }, []);

  const title = kind === 'skip'
    ? 'Discard these answers and skip today?'
    : kind === 'change-date'
    ? 'Choose another day without saving?'
    : 'Leave without saving your Daily Pulse?';
  const body = kind === 'skip'
    ? `You have answered ${count} ${count === 1 ? 'question' : 'questions'}${note.trim() ? ' and started a note' : ''} for ${dateLabel}. Skipping today discards them. You can still backfill this date later this week.`
    : kind === 'change-date'
    ? `Your changes for ${dateLabel} aren't saved. Choosing another day discards these changes. Your previously saved entries and goal updates stay saved.`
    : `Your answers for ${targetDate === today ? 'today' : dateLabel} aren't saved yet. Keep editing to finish, or close and your goals stay saved either way.`;

  return (
    <div className="daily-pulse-confirmation__backdrop">
      <div
        className="daily-pulse-confirmation"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="daily-pulse-confirmation-title"
        aria-describedby="daily-pulse-confirmation-body"
      >
        <span className="daily-pulse__eyebrow">Unsaved Daily Pulse</span>
        <h2 id="daily-pulse-confirmation-title" className="daily-pulse-confirmation__title">{title}</h2>
        <p id="daily-pulse-confirmation-body" className="daily-pulse-confirmation__body">{body}</p>
        <div className="daily-pulse-confirmation__actions">
          <button ref={keepEditingRef} type="button" className="daily-pulse__primary" onClick={onCancel}>
            Keep editing
          </button>
          <button type="button" className="daily-pulse__secondary" onClick={onConfirm}>
            {kind === 'skip' ? 'Discard and skip' : kind === 'change-date' ? 'Discard and choose day' : 'Discard and close'}
          </button>
        </div>
      </div>
    </div>
  );
};

const RatingControl = ({
  question,
  value,
  onChange,
}: {
  question: DailyPulseQuestion;
  value?: number;
  onChange: (value: number) => void;
}) => {
  const answered = typeof value === 'number';
  const safeValue = answered ? value : 5;
  // Unanswered ratings park a hollow thumb at 5 over an empty track so they don't read as a 5.
  const fill = answered ? ((safeValue - 1) / 9) * 100 : 0;
  return (
    <div
      className={`daily-pulse-rating ${answered ? 'daily-pulse-rating--answered' : ''}`}
      data-testid={`daily-pulse-rating-${question.key}`}
    >
      <input
        className="daily-pulse-rating__input"
        type="range"
        min={1}
        max={10}
        step={1}
        value={safeValue}
        aria-label={question.prompt}
        aria-valuetext={answered ? `${safeValue} of 10` : 'Not answered'}
        style={{ '--daily-pulse-fill': `${fill}%` } as React.CSSProperties}
        onChange={(event) => onChange(Number(event.target.value))}
        onPointerUp={(event) => onChange(Number(event.currentTarget.value))}
        onKeyDown={(event) => {
          const next = event.key === 'Home' ? 1 : event.key === 'End' ? 10
            : ['ArrowRight', 'ArrowUp'].includes(event.key) ? Math.min(10, safeValue + 1)
            : ['ArrowLeft', 'ArrowDown'].includes(event.key) ? Math.max(1, safeValue - 1)
            : ['Enter', ' '].includes(event.key) ? safeValue : undefined;
          if (next === undefined) return;
          event.preventDefault();
          onChange(next);
        }}
      />
      <div className="daily-pulse-rating__endpoints" aria-hidden="true"><span>1</span><span>10</span></div>
    </div>
  );
};

const BooleanControl = ({
  question,
  value,
  onChange,
}: {
  question: DailyPulseQuestion;
  value?: boolean;
  onChange: (value: boolean) => void;
}) => {
  const yesRef = useRef<HTMLButtonElement>(null);
  const noRef = useRef<HTMLButtonElement>(null);
  return (
    <div className="daily-pulse-boolean" role="radiogroup" aria-label={`${question.prompt} Choose yes or no.`}
      onKeyDown={(event) => {
        if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const next = event.key === 'Home' ? true : event.key === 'End' ? false : event.target !== yesRef.current;
        onChange(next);
        (next ? yesRef : noRef).current?.focus();
      }}>
      <button
        ref={yesRef}
        type="button"
        role="radio"
        aria-checked={value === true}
        tabIndex={value === false ? -1 : 0}
        className={`daily-pulse-boolean__option ${value === true ? 'daily-pulse-boolean__option--selected' : ''}`}
        onClick={() => onChange(true)}
      >
        Yes
      </button>
      <button
        ref={noRef}
        type="button"
        role="radio"
        aria-checked={value === false}
        tabIndex={value === false ? 0 : -1}
        className={`daily-pulse-boolean__option ${value === false ? 'daily-pulse-boolean__option--selected' : ''}`}
        onClick={() => onChange(false)}
      >
        No
      </button>
    </div>
  );
};

export const DailyPulseForm = ({
  questions,
  answers,
  note,
  targetDate,
  today,
  initialOutcome = null,
  journeySegments = [],
  progressLabel,
  saving = false,
  saved = false,
  dirty = true,
  error = '',
  confirmation = null,
  noteOnly = false,
  onAnswer,
  onNoteChange,
  onSave,
  onSkip,
  onClose,
  onCustomize,
  onFillAnother,
  onCancelConfirmation,
  onConfirmDiscard,
}: DailyPulseFormProps) => {
  const headingRef = useRef<HTMLHeadingElement>(null);
  const noteRef = useRef<HTMLTextAreaElement>(null);
  const isToday = targetDate === today;
  const isEditing = initialOutcome === 'saved';
  const isBackfill = !isToday && !isEditing;
  const canSkipToday = isToday && initialOutcome === null;
  const hasInput = answeredCount(answers) > 0 || Boolean(note.trim());
  const hasInvalidAnswer = questions.some((question) => Boolean(metricError(question, answers[question.key])));
  const dateLabel = useMemo(() => formatDate(targetDate), [targetDate]);
  const dateLine = `${dateLabel}${isEditing ? ' · Editing' : !isToday ? ' · Past day' : ''}`;
  const prompt = noteOnly
    ? 'What would you like to remember about today?'
    : isBackfill ? 'Add what you remember from this day.' : '';
  const primaryLabel = error
    ? 'Try again'
    : saved
    ? 'Saved'
    : saving
      ? 'Saving…'
      : isEditing
        ? 'Save changes'
        : 'Save Pulse';

  useEffect(() => {
    headingRef.current?.focus();
  }, [targetDate]);

  useLayoutEffect(() => {
    const element = noteRef.current;
    if (!element) return;

    const resize = () => {
      element.style.height = 'auto';
      element.style.height = `${Math.min(Math.max(element.scrollHeight, 64), 140)}px`;
    };
    resize();

    let width = element.clientWidth;
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => {
      if (element.clientWidth === width) return;
      width = element.clientWidth;
      resize();
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [note, targetDate]);

  return (
    <div className="daily-pulse" data-testid="daily-pulse-form">
      <header className="daily-pulse__header">
        <div className="daily-pulse__header-topline">
          <span className="daily-pulse__date">{dateLine}</span>
          <div className="daily-pulse__header-actions">
            <button
              type="button"
              aria-label="Customize questions"
              title="Customize questions"
              className="daily-pulse__customize"
              onClick={onCustomize}
              disabled={saving || saved}
            >
              <SlidersHorizontal size={17} aria-hidden="true" />
            </button>
            <button type="button" aria-label="Done for now — close check-in" className="daily-pulse__close" disabled={saving || saved} onClick={onClose}>
              <X size={17} />
            </button>
          </div>
        </div>
        <h2 ref={headingRef} tabIndex={-1} className="daily-pulse__title">Daily Pulse</h2>
        {journeySegments.length ? (
          <div className="daily-pulse__journey">
            <div className="daily-pulse__segments" role="group" aria-label={progressLabel}>
              {journeySegments.map((segment) => (
                <span key={segment.key} className={`daily-pulse__segment daily-pulse__segment--${segment.status}`} />
              ))}
            </div>
            <span className="daily-pulse__progress-label">{progressLabel}</span>
          </div>
        ) : null}
      </header>

      <div className="daily-pulse__body" role="region" aria-label="Daily Pulse questions" tabIndex={0}>
        <fieldset className="daily-pulse__fields" disabled={saving || saved} aria-label="Daily Pulse answers">
          {error ? (
            <div className="daily-pulse__error" role="alert">
              <AlertCircle size={18} />
              <span><strong>We couldn't save your Daily Pulse.</strong><br />Your answers are still here. Nothing was lost.</span>
            </div>
          ) : null}

          {prompt ? <div className="daily-pulse__intro"><p>{prompt}</p></div> : null}

          {questions.length ? (
            <div className="daily-pulse__rows">
              {questions.map((question) => {
                const value = answers[question.key];
                return (
                  <div
                    className={`daily-pulse__row daily-pulse__row--${question.response_type}`}
                    key={question.key}
                    role="group"
                    aria-label={`${question.prompt} response`}
                  >
                    <div className="daily-pulse__question-copy">
                      <span className="daily-pulse__question">{question.prompt}</span>
                      {question.response_type === 'rating' ? (
                        <output className={`daily-pulse-rating__value ${typeof value === 'number' ? 'daily-pulse-rating__value--answered' : ''}`} aria-live="polite">
                          {typeof value === 'number' ? value : '–'}
                        </output>
                      ) : null}
                    </div>
                    {question.response_type === 'rating' ? (
                      <RatingControl
                        question={question}
                        value={typeof value === 'number' ? value : undefined}
                        onChange={(nextValue) => onAnswer(question.key, nextValue)}
                      />
                    ) : question.response_type === 'boolean' ? (
                      <BooleanControl
                        question={question}
                        value={typeof value === 'boolean' ? value : undefined}
                        onChange={(nextValue) => onAnswer(question.key, nextValue)}
                      />
                    ) : question.response_type === 'duration' ? (
                      <DurationControl
                        key={`${targetDate}-${question.key}`}
                        question={question}
                        value={value}
                        onChange={(nextValue) => onAnswer(question.key, nextValue)}
                      />
                    ) : (
                      <MetricControl
                        key={`${targetDate}-${question.key}`}
                        question={question}
                        value={value}
                        onChange={(nextValue) => onAnswer(question.key, nextValue)}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          ) : null}

          <div className="daily-pulse__note-field">
            <div className="daily-pulse__note-label">
              <label htmlFor="daily-pulse-note">Anything else you want to remember about today?</label>
              <span>Optional</span>
            </div>
            <textarea
              ref={noteRef}
              id="daily-pulse-note"
              rows={2}
              maxLength={2000}
              placeholder="A line or two is plenty."
              value={note}
              onChange={(event) => onNoteChange(event.target.value)}
            />
          </div>
        </fieldset>
      </div>

      <footer className="daily-pulse__footer">
        <button
          type="button"
          className={`daily-pulse__primary ${saving && !saved ? 'daily-pulse__primary--saving' : ''} ${saved ? 'daily-pulse__primary--saved' : ''}`}
          disabled={!hasInput || hasInvalidAnswer || !dirty || saving || saved}
          aria-busy={saving}
          onClick={onSave}
        >
          {saved
            ? <><Check size={17} /> {primaryLabel}</>
            : saving
              ? <><span className="daily-pulse__spinner" aria-hidden="true" /> {primaryLabel}</>
              : primaryLabel}
        </button>
        <div className="daily-pulse__footer-actions">
          {canSkipToday ? (
            <button type="button" className="daily-pulse__secondary" disabled={saving || saved} onClick={onSkip}>Skip for today</button>
          ) : isBackfill && onFillAnother ? (
            <button type="button" className="daily-pulse__secondary" disabled={saving || saved} onClick={onFillAnother}>Fill another missed day</button>
          ) : null}
          <button type="button" className="daily-pulse__cancel" disabled={saving || saved} onClick={onClose}>Cancel</button>
        </div>
      </footer>

      {confirmation ? (
        <DailyPulseConfirmation
          kind={confirmation}
          answers={answers}
          note={note}
          targetDate={targetDate}
          today={today}
          onCancel={onCancelConfirmation}
          onConfirm={onConfirmDiscard}
        />
      ) : null}
    </div>
  );
};
